import { Category } from './category';
import { TreeEntry, isTreeFolder } from '../util/document-list-to-tree';
import { useSortedTreeEntries } from '../util/sorted-tree-entries';

interface CategoryContentProps {
  /** The tree entry containing children to render */
  content: TreeEntry;
  /** Callback function called when a category is clicked */
  onClick: (id: string) => void;
}

/**
 * Renders a list of category items from a tree structure
 *
 * This component takes a tree entry and renders its children as clickable
 * category items. It automatically sorts the children alphabetically and
 * provides keyboard navigation support for accessibility.
 *
 * @param props - Component props containing content and click handler
 * @returns Rendered list of category items or null if no children exist
 */
export const CategoryContent = ({ content, onClick }: CategoryContentProps) => {
  const sortedItems = useSortedTreeEntries(content);

  if (sortedItems.length === 0) {
    return null;
  }
  return (
    <>
      {sortedItems.map(([, entry]) => (
        <div key={entry.id} className="my-2">
          {isTreeFolder(entry) ? (
            <Category title={entry.toDisplayString()} entry={entry} />
          ) : (
            <div
              className="my-4 cursor-pointer"
              onClick={(event) => {
                event.stopPropagation();
                onClick(entry.id);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  onClick(entry.id);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <Category
                title={entry.toDisplayString()}
                entry={entry}
                mode="accordion-header"
                showToggle={false}
              />
            </div>
          )}
        </div>
      ))}
    </>
  );
};
